/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { Fragment, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { Listbox } from '@headlessui/react'
import { CheckIcon } from '@heroicons/react/24/solid'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'
import T from 'components/T'
import { PhoneCountry } from 'utils/countryUtils'
import { useTranslation } from 'react-i18next'
import { Overlay } from 'react-overlays'
import useGetCountryCode from 'utils/hooks/useGetCountryCode'
import { getEnglishCountryNameFromAnyCountryValue } from 'utils/hooks/useGetCountryCode'
import UiIcon from 'components/Ui/UiIcon'

interface CountrySelectCountryComponentProps {
    countryCode?: string
    style?: 'normal' | 'decorated'
}

const CountrySelectCountryComponent = (
    props: CountrySelectCountryComponentProps
) => {
    const [countryFlag, setCountryFlag] = useState(null)

    useEffect(() => {
        const loadCountryFlag = async () => {
            try {
                const countryFlagModule = await import(
                    `components/CountryIcons/3x2/${props.countryCode}.svg`
                )
                setCountryFlag(countryFlagModule.default)
            } catch (error) {
                console.error(
                    `Error loading country flag for ${props.countryCode}:`,
                    error
                )
            }
        }

        if (props.countryCode) {
            loadCountryFlag()
        }

        return () => {
            setCountryFlag(null)
        }
    }, [props.countryCode])

    return (
        <div
            className={clsx(
                'CountryFlag flex items-center justify-center overflow-hidden',
                props?.style === 'decorated' &&
                    'decorated min-w-10 h-10 rounded-l-lg',
                !props?.style &&
                    'not-decorated w-6 h-4 border border-gray-200 dark:border-gray-800 rounded-sm'
            )}
        >
            {countryFlag && (
                <img
                    className={clsx('object-cover h-full w-full')}
                    alt={`${props.countryCode} flag`}
                    src={countryFlag}
                />
            )}
        </div>
    )
}

interface ReservationDetailCountryProps {
    options: PhoneCountry[]
    defaultCountry: any
    value?: any
    register: any
    name: string
    label: string
    className?: string
    classNameContainer?: string
    classNameInput?: string
    required?: boolean
    errors?: any
    disabled?: boolean
    setValue?: any
    style?: 'normal' | 'inline'
    resetValue?: boolean
    setResetCountry?: (val: boolean) => void
}

const ReservationDetailCountry = (props: ReservationDetailCountryProps) => {
    const { t } = useTranslation()
    const getCountryCode = useGetCountryCode()
    const [selectedCountry, setSelectedCountry] = useState<PhoneCountry | null>(
        null
    )
    const selectedOptionRef = useRef<HTMLLIElement>(null)
    const targetRef = useRef<HTMLButtonElement>(null)
    const [show, setShow] = useState(false)
    const options = props?.options

    const searchCountryByName = (countryCode: string): PhoneCountry => {
        const countryIndex = props.options.findIndex(
            (country) => country.textCode == countryCode
        )
        return props.options[countryIndex]
    }

    const setSelectedCountryHandler = (country: PhoneCountry) => {
        setSelectedCountry(country)
        setShow(false)
        if (country) {
            getEnglishCountryNameFromAnyCountryValue(country.textCode, country.name).then(
                (englishCountryName) => {
                    props.setValue(props.name, englishCountryName)
                }
            )
        }
    }

    const checkCountry = async (name: string) => {
        const checkedCountryCode =
            await getCountryCode.getCountryCodeFromCountryName(name)
        // if checkedCountryCode not search code, set default country code
        const searchedCountry = searchCountryByName(
            checkedCountryCode ? checkedCountryCode : props.defaultCountry
        )
        setSelectedCountryHandler(searchedCountry)
    }

    useEffect(() => {
        if (props?.value) {
            checkCountry(props.value).then(() => {})
        }
        if (!props?.value) {
            setSelectedCountryHandler(searchCountryByName(props.defaultCountry))
        }
    }, [props?.value])

    useEffect(() => {
        if (props?.resetValue && props?.setResetCountry) {
            props?.value
                ? checkCountry(props.value).then(() => {})
                : setSelectedCountryHandler(
                      searchCountryByName(props.defaultCountry)
                  )
            props.setResetCountry(false)
        }
    }, [props?.resetValue])

    return (
        <div
            className={clsx(
                'ReservationDetailCountry w-full relative',
                props?.style === 'inline' &&
                    'sm:flex sm:flex-row sm:space-x-6 sm:items-center sm:flex-wrap',
                props.className
            )}
        >
            <label
                className={clsx(
                    'font-medium mb-1.5 flex text-sm text-gray-700 dark:text-gray-300',
                    props.errors?.[props.name]
                        ? 'text-red-500 dark:text-red-400'
                        : ''
                )}
                htmlFor={props.name}
            >
                <span>{t(props.label)}</span>
            </label>

            <div
                className={clsx(
                    'flex flex-row relative w-full flex-1',
                    props?.style === 'inline' && 'sm:w-2/3! sm:flex-none!'
                )}
            >
                <Listbox
                    disabled={props.disabled}
                    value={selectedCountry}
                    onChange={setSelectedCountryHandler}
                >
                    {({ open }) => (
                        <div className="w-full">
                            <Listbox.Button
                                ref={targetRef}
                                className={clsx(
                                    'relative h-10 flex items-center justify-between w-full rounded-lg border border-gray-300 dark:border-gray-800 text-sm font-medium transition-colors shadow-sm',
                                    'pr-3',
                                    props.disabled
                                        ? 'bg-gray-100 dark:bg-gray-900 text-gray-500 cursor-not-allowed'
                                        : 'bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100',
                                    props.errors?.[props.name] &&
                                        !open &&
                                        !selectedCountry &&
                                        'border-red-500 focus:border-red-500'
                                )}
                                onClick={() => setShow(!show)}
                            >
                                <div
                                    className={
                                        'flex flex-row items-center w-full overflow-hidden'
                                    }
                                >
                                    <CountrySelectCountryComponent
                                        countryCode={selectedCountry?.textCode}
                                        style={'decorated'}
                                    />
                                    <span
                                        className={clsx(
                                            'ml-3 block truncate font-normal'
                                        )}
                                    >
                                        {selectedCountry?.name}
                                    </span>
                                </div>
                                <span className="pointer-events-none flex h-full pl-1 items-center">
                                    <UiIcon
                                        name={'chevron-down'}
                                        strokeWidth={2}
                                        className={
                                            'size-4 flex-none text-gray-500 dark:text-gray-400'
                                        }
                                    />
                                </span>
                            </Listbox.Button>
                            <Overlay
                                placement="bottom-start"
                                show={show}
                                target={targetRef.current}
                                rootClose
                                onHide={() => setShow(false)}
                            >
                                {({ props, placement }) => (
                                    <Listbox.Options
                                        {...props}
                                        style={{
                                            ...props.style,
                                            transition:
                                                'maxHeight 0.3s ease-in-out',
                                            // @ts-ignore
                                            width: targetRef?.current
                                                ? targetRef.current.getBoundingClientRect()
                                                      .width
                                                : '100%',
                                        }}
                                        className={clsx(
                                            'transition-all duration-300 ease-in h-auto shadow-xl z-9999 max-h-60 w-full overflow-auto rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 py-1 text-base',
                                            placement
                                        )}
                                        unmount={false}
                                        onLoad={() => {
                                            if (
                                                selectedCountry &&
                                                selectedOptionRef?.current
                                            ) {
                                                selectedOptionRef.current.scrollIntoView(
                                                    {
                                                        behavior: 'smooth',
                                                        block: 'nearest',
                                                    }
                                                )
                                            }
                                        }}
                                    >
                                        {options.map((option) => (
                                            <Listbox.Option
                                                key={option.textCode}
                                                as={Fragment}
                                                value={option}
                                            >
                                                {({ selected, active }) => (
                                                    <li
                                                        ref={
                                                            selected
                                                                ? selectedOptionRef
                                                                : null
                                                        }
                                                        className={clsx(
                                                            'relative flex flex-row cursor-pointer select-none py-2 px-4 text-sm transition-colors',
                                                            active
                                                                ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100'
                                                                : 'text-gray-900 dark:text-gray-100',
                                                            selected &&
                                                                'font-medium'
                                                        )}
                                                    >
                                                        {selected && (
                                                            <span
                                                                className={
                                                                    'absolute left-2 flex items-center'
                                                                }
                                                            >
                                                                <CheckIcon
                                                                    className={
                                                                        'w-4 h-4 text-primary-600 dark:text-primary-400'
                                                                    }
                                                                />
                                                            </span>
                                                        )}
                                                        <div
                                                            className={clsx(
                                                                'flex items-center truncate',
                                                                !selected &&
                                                                    'pl-5'
                                                            )}
                                                        >
                                                            <CountrySelectCountryComponent
                                                                countryCode={
                                                                    option.textCode
                                                                }
                                                            />
                                                            <span
                                                                className={clsx(
                                                                    'ml-1 block truncate'
                                                                )}
                                                            >
                                                                {option.name}
                                                            </span>
                                                        </div>
                                                    </li>
                                                )}
                                            </Listbox.Option>
                                        ))}
                                    </Listbox.Options>
                                )}
                            </Overlay>
                        </div>
                    )}
                </Listbox>
                <div className={'relative grow hidden'}>
                    <input
                        {...props.register(props.name)}
                        className={clsx('hidden')}
                        defaultValue={props?.value}
                        disabled={props.disabled}
                        name={props.name}
                        required={props.required}
                        type={'hidden'} // ensuring it remains hidden
                        id={props.name}
                    />
                </div>
            </div>
            {props.errors?.[props.name]?.message && (
                <div
                    className={clsx(
                        'text-red-500 text-sm mt-1 flex flex-row items-center shrink-0'
                    )}
                >
                    <div
                        className={clsx(
                            'shrink-0 flex flex-row items-center text-xxs md:text-base w-full'
                        )}
                    >
                        <ExclamationCircleIcon
                            className={
                                'size-3.5 md:size-4.5 flex-none mr-0.5'
                            }
                        />
                        <T>{props.errors?.[props.name]?.message}</T>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ReservationDetailCountry
